import type { ClientMessage, ServerMessage } from '@phidio/protocol';

export type SignalingStatus = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'closed';

export interface SignalingHandlers {
  onMessage: (message: ServerMessage) => void;
  onStatus: (status: SignalingStatus) => void;
}

function defaultUrl(): string {
  const configured = import.meta.env.VITE_SIGNALING_URL;
  if (configured) return configured;
  const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${location.host}/ws`;
}

/**
 * WebSocket transport with exponential backoff reconnection and automatic
 * heartbeat replies. All application logic lives in the engine; this class only
 * owns the socket lifecycle.
 */
export class Signaling {
  private socket: WebSocket | null = null;
  private reconnectTimer: number | null = null;
  private attempts = 0;
  private closedByUser = false;
  private readonly url: string;
  private readonly handlers: SignalingHandlers;

  status: SignalingStatus = 'idle';

  constructor(handlers: SignalingHandlers, url = defaultUrl()) {
    this.handlers = handlers;
    this.url = url;
  }

  get isOpen(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  connect(): void {
    if (this.socket && this.socket.readyState <= WebSocket.OPEN) return;
    this.closedByUser = false;
    this.setStatus(this.attempts === 0 ? 'connecting' : 'reconnecting');

    const socket = new WebSocket(this.url);
    this.socket = socket;

    socket.onopen = () => {
      this.attempts = 0;
      this.setStatus('open');
    };

    socket.onmessage = (event) => {
      if (typeof event.data !== 'string') return;
      let message: ServerMessage;
      try {
        message = JSON.parse(event.data) as ServerMessage;
      } catch {
        return;
      }
      if (message.t === 'ping') {
        this.send({ t: 'pong' });
        return;
      }
      this.handlers.onMessage(message);
    };

    socket.onerror = () => socket.close();

    socket.onclose = (event) => {
      this.socket = null;
      if (this.closedByUser || event.code === 4002 || event.code === 4003) {
        this.setStatus('closed');
        return;
      }
      this.scheduleReconnect();
    };
  }

  send(message: ClientMessage): boolean {
    if (!this.isOpen) return false;
    this.socket!.send(JSON.stringify(message));
    return true;
  }

  close(): void {
    this.closedByUser = true;
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.socket?.close(1000, 'bye');
    this.socket = null;
    this.setStatus('closed');
  }

  private scheduleReconnect(): void {
    this.setStatus('reconnecting');
    this.attempts += 1;
    const base = Math.min(1000 * 2 ** (this.attempts - 1), 15_000);
    const delay = base * (0.7 + Math.random() * 0.6);
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private setStatus(status: SignalingStatus): void {
    if (this.status === status) return;
    this.status = status;
    this.handlers.onStatus(status);
  }
}
